import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';

export enum Direction {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
}

export interface IAnimationConfig {
  frames?: number[];
  start?: number;
  end?: number;
  frameRate?: number;
  repeat?: number;
}

export class AnimationUtils {
  static paceBackAndForth(
    container: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    distance: number,
    duration: number,
  ) {
    const startX = container.x;
    const endX = startX + distance;

    const tween = scene.tweens.add({
      targets: container,
      x: endX,
      ease: 'Linear',
      duration,
      yoyo: true,
      repeat: -1, // Infinite loop
      onStart: () => {
        this.startWalkingBackAndForth(scene, container, false);
      },
      onYoyo: () => {
        this.startWalkingBackAndForth(scene, container, true);
      },
      onRepeat: () => {
        this.startWalkingBackAndForth(scene, container, false);
      },
    });
    return tween;
  }

  static paceUpDown(
    container: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    distance: number,
    duration: number,
  ) {
    const startY = container.y;
    const endY = startY + distance;

    const tween = scene.tweens.add({
      targets: container,
      y: endY,
      ease: 'Linear',
      duration: duration,
      yoyo: true,
      repeat: -1, // Infinite loop
      onStart: () => {
        this.startWalkingUpDown(container, 'up');
      },
      onYoyo: () => {
        this.startWalkingUpDown(container, 'down');
      },
      onRepeat: () => {
        this.startWalkingUpDown(container, 'up');
      },
    });

    return tween;
  }

  static getRandomSatisfiedAnimationKey() {
    const animations = ['wave', 'meditate', 'sing'];
    return animations[Math.floor(Math.random() * animations.length)];
  }

  static successFeedback(container: Phaser.GameObjects.Container) {
    const key = this.getRandomSatisfiedAnimationKey();
    // Set NPC to face the player (may need adjustment based on actual player position)
    container.each((sprite: Phaser.GameObjects.Sprite) => {
      sprite.flipX = true; // or determine flip based on player's position relative to NPC

      // Do a thumbs-up , then back to an idle animation.
      sprite.anims?.play(`${sprite.texture?.key}-thumbsup`);
      sprite.anims?.playAfterDelay(`${sprite.texture.key}-${key}`, 500);
    });
  }

  static setWalking(
    container: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    direction: string,
  ) {
    container.list.forEach((child: any) => {
      const animKey = `${child.texture.key}-walk-${direction}`;
      if (scene.anims.exists(animKey)) {
        switch (direction) {
          case 'left':
            child.setFlipX(true);
            break;
          case 'right':
            child.setFlipX(false);
            break;
          case 'up':
          case 'down':
            break;
        }

        child.anims.play(animKey, true);
      }
    });
  }

  static setGenericAnimation(
    container: Phaser.GameObjects.Container,
    scene: Phaser.Scene,
    animKey: string,
  ) {
    container.each((sprite: Phaser.GameObjects.Sprite) => {
      const animationKey = `${sprite.texture.key}-${animKey}`;
      if (scene.anims.exists(animationKey)) {
        sprite.anims.play(animationKey, true);
      }
    });
  }

  static setSatisfied(container: Phaser.GameObjects.Container) {
    // List of possible animation keys
    const animations = ['wave', 'meditate', 'sing'];

    // Randomly select an animation from the list
    const randomAnimation =
      animations[Math.floor(Math.random() * animations.length)];

    // Play the randomly selected animation for each sprite in the container
    container.each((sprite: Phaser.GameObjects.Sprite) => {
      sprite?.anims?.play(`${sprite.texture.key}-${randomAnimation}`);
    });
  }

  static async setHurt(
    container: Phaser.GameObjects.Container,
    animationConfig?: Omit<Phaser.Types.Animations.PlayAnimationConfig, 'key'>,
    onHurtFinished?: () => void,
  ) {
    container.each((sprite: Phaser.GameObjects.Sprite) => {
      const config: Phaser.Types.Animations.PlayAnimationConfig = {
        key: `${sprite.texture.key}-hurt`,
        ...(animationConfig || {}),
      };
      sprite.anims.play(config);
    });

    await waitForSeconds(container.scene.time, 1);
    onHurtFinished?.();
  }

  static setIdle(
    container: Phaser.GameObjects.Container,
    direction: Direction,
  ) {
    container.each((sprite: Phaser.GameObjects.Sprite) => {
      if (sprite && sprite.texture?.key !== '__MISSING') {
        switch (direction) {
          case 'up':
            sprite.anims?.play(`${sprite.texture.key}-idle-up`);
            break;
          case 'down':
            sprite.anims?.play(`${sprite.texture.key}-idle-down`);
            break;
          case 'left':
            sprite.flipX = false;
            sprite.anims?.play(`${sprite.texture.key}-idle-left`);
            break;
          case 'right':
            sprite.flipX = true;
            sprite.anims?.play(`${sprite.texture.key}-idle-left`);
            break;
        }
      }
    });
  }

  static setSinging(
    container: Phaser.GameObjects.Container,
    animationConfig?: Omit<Phaser.Types.Animations.PlayAnimationConfig, 'key'>,
  ): Promise<void> {
    return new Promise((resolve) => {
      let animationsToComplete = 0;

      container.each((sprite: Phaser.GameObjects.Sprite) => {
        if (sprite && sprite.texture.key !== '__MISSING') {
          const config: Phaser.Types.Animations.PlayAnimationConfig = {
            key: `${sprite.texture.key}-sing`,
            ...(animationConfig || {}),
          };

          if (sprite.anims) {
            animationsToComplete++;

            sprite.anims.play(config, true).once('animationcomplete', () => {
              animationsToComplete--;

              if (animationsToComplete === 0) {
                resolve();
              }
            });
          }
        }
      });

      if (animationsToComplete === 0) {
        resolve();
      }
    });
  }

  static async setDefeated(
    container: Phaser.GameObjects.Container,
    animationConfig?: Omit<Phaser.Types.Animations.PlayAnimationConfig, 'key'>,
  ) {
    container.list.forEach(async (child: any) => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        const config: Phaser.Types.Animations.PlayAnimationConfig = {
          key: `${child.texture.key}-defeated`,
          ...(animationConfig || {}),
          repeat: -1,
        };
        child.anims.play(config);

        const defeatedFlip = setInterval(() => {
          child.setFlipX(!child.flipX);
        }, 245);
        await waitForSeconds(container.scene.time, 4);
        clearInterval(defeatedFlip);
      }
    });
  }

  static setAttacking(
    container: Phaser.GameObjects.Container,
    animationConfig?: Omit<Phaser.Types.Animations.PlayAnimationConfig, 'key'>,
    flipX?: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      let animationsToComplete = 0;

      container.each((sprite: Phaser.GameObjects.Sprite) => {
        if (sprite && sprite.texture.key !== '__MISSING') {
          const config: Phaser.Types.Animations.PlayAnimationConfig = {
            key: `${sprite.texture.key}-attack`,
            ...(animationConfig || {}),
          };

          sprite.flipX = flipX || false;
          if (sprite.anims) {
            animationsToComplete++;

            sprite.anims.play(config, true).once('animationcomplete', () => {
              animationsToComplete--;

              if (animationsToComplete === 0) {
                resolve();
              }
            });
          }
        }
      });

      if (animationsToComplete === 0) {
        resolve();
      }
    });
  }

  private static startWalkingBackAndForth(
    scene: Phaser.Scene,
    container: Phaser.GameObjects.Container,
    flipX: boolean,
  ) {
    container.list.forEach((child: any) => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        if (scene.anims.exists(`${child.texture.key}-walk-right`)) {
          child.anims.play(`${child.texture.key}-walk-right`, true);
          child.flipX = flipX;
        } else {
          console.error(`Animation not found: ${child.texture.key}-walk-right`);
        }
      }
    });
  }

  private static startWalkingUpDown(
    container: Phaser.GameObjects.Container,
    direction: 'up' | 'down',
  ) {
    const animKeySuffix = direction === 'up' ? '-walk-up' : '-walk-down';
    container.list.forEach((child: any) => {
      if (child instanceof Phaser.GameObjects.Sprite) {
        child.anims.play(`${child.texture.key}${animKeySuffix}`, true);
      }
    });
  }
}
